import React, { useContext, useState } from 'react';
import { pick } from 'lodash';
import { Button } from '@onward-delivery/design';
import { UserContext } from '@/components/App';
import { CorpAdminContext } from '@/components/CorporateUsers/store';
import IntegrationRow from '@/components/CorporateUsers/modals/IntegrationRow';
import IntegrationsModal from '@/components/CorporateUsers/modals/IntegrationsModal';
import { CardRow, StatusTag, SystemCard } from './blocks';
import { TMS_TYPES, lastSyncOf, requiredFieldLabelsOf, tmsMethodOf, typeLabelOf } from './utils';

const OVERRIDE_IDENTITY_KEYS = [
    'override_id',
    'carrier_id',
    'middle_mile_carrier_id',
    'client_id',
    'organization_id',
    'is_shipper_default',
];

const ConnectedTms = ({ integration, defaultIntegrations }) => {
    const { client: carrier } = useContext(UserContext);
    const [configuring, setConfiguring] = useState(false);

    const typeLabel = typeLabelOf(integration.type);

    const configureModal = configuring ? (
        <IntegrationsModal
            open
            onClose={() => setConfiguring(false)}
            defaultIntegrations={defaultIntegrations}
            carrierId={carrier.client_id}
        />
    ) : null;

    return (
        <SystemCard
            badge={typeLabel.slice(0, 2).toUpperCase()}
            badgeClassName="bg-teal-800"
            title={typeLabel}
            subtitle="their TMS — the one system their whole record syncs from"
            actions={
                <Button variant="outline" size="sm" onClick={() => setConfiguring(true)}>
                    Configure
                </Button>
            }
        >
            <CardRow label="Method">{tmsMethodOf(integration)}</CardRow>
            <CardRow label="Status">
                <StatusTag status={integration.status} />
            </CardRow>
            <CardRow label="Last sync">{lastSyncOf(integration.last_success_at)}</CardRow>
            {configureModal}
        </SystemCard>
    );
};

// Saving the draft writes the org default; a MANUAL placeholder default is updated in place rather than duplicated.
const TmsPicker = ({ placeholder, disabled }) => {
    const { client: carrier } = useContext(UserContext);
    const { state } = useContext(CorpAdminContext);
    const [draft, setDraft] = useState(null);

    const pickType = (type) => {
        const newDefault = {
            carrier_id: carrier.client_id,
            client_id: carrier.client_id,
            organization_id: state.organizationId,
            is_shipper_default: true,
        };
        const placeholderIdentity = placeholder ? pick(placeholder, OVERRIDE_IDENTITY_KEYS) : null;
        const identity = draft || placeholderIdentity || newDefault;

        setDraft({ ...identity, type, config: {} });
    };

    const chips = TMS_TYPES.map((type) => {
        const isPicked = type === draft?.type;

        return (
            <Button
                key={type}
                variant="outline"
                size="sm"
                aria-pressed={isPicked}
                disabled={disabled}
                className={isPicked ? 'border-primary text-primary' : ''}
                onClick={() => pickType(type)}
            >
                {typeLabelOf(type)}
            </Button>
        );
    });

    let draftForm = null;
    if (draft) {
        const requiredLabels = requiredFieldLabelsOf(draft.type);
        draftForm = (
            <>
                <IntegrationRow
                    key={draft.type}
                    integration={draft}
                    onSaved={() => setDraft(null)}
                    onDiscard={() => setDraft(null)}
                    onWritten={(overrideId) => setDraft((current) => ({ ...current, override_id: overrideId }))}
                />
                <span className="w-fit rounded-md border border-slate-200 bg-white px-2 py-1 text-[12px] text-slate-500">
                    Needs: <span className="font-semibold text-slate-900">{requiredLabels.join(' · ')}</span>
                </span>
            </>
        );
    }

    return (
        <SystemCard
            badge="?"
            badgeClassName="bg-slate-400"
            title="No TMS connected"
            subtitle="one system per carrier — pick theirs and enter what it asks for"
        >
            <div className="flex flex-col gap-3 bg-slate-50 p-4">
                <div className="flex flex-wrap gap-2">{chips}</div>
                {draftForm}
            </div>
        </SystemCard>
    );
};

/**
 * The carrier's TMS: the connected org default, or a picker to set one up. `disabled` turns the picker's types off,
 * while the carrier is marked No External Integrations Required.
 * @param {{ defaultIntegrations: object[], disabled?: boolean }} props `defaultIntegrations` are the org's
 *   integration_overrides defaults for the carrier
 */
const TmsCard = ({ defaultIntegrations, disabled = false }) => {
    const connected = defaultIntegrations.find((integration) => integration.type !== 'MANUAL');
    if (connected) return <ConnectedTms integration={connected} defaultIntegrations={defaultIntegrations} />;

    const placeholder = defaultIntegrations.find((integration) => integration.type === 'MANUAL');

    return <TmsPicker placeholder={placeholder} disabled={disabled} />;
};

export default TmsCard;
